import _ from 'lodash';
import {
    FILTER_TOGGLE,
    FILTER_CLOSE,
    FILTER_APPLY,
    FILTER_PARAMETER_TOGGLE,
    FILTER_OPTION_SELECT,
    FILTER_INIT,
    FILTER_CONFIG_REQUEST,
    FILTER_CONFIG_UPDATE,
} from '../actions';

const initialState = {
    url: '',
    defaults: {},
    parameters: [],
    opened: false,
    initialSelection: {},
    selection: {},
    applied: {},
    expanded: [],
    hiddenParams: [],
    status: []
};

/**
 * Iteration conditions array which is set up like this:
 * conditions: [ { parameter1: [ value1, value2 ... ], ...}, ...]
 * - All objects in arrays are considered conjuncted with OR operator, while
 * - All objects in containing object are considered conjuncted with AND operator
 *
 * Example:
 * conditions: [
 *    {
 *        raceType: ['flat', 'jumps'],
 *        age: '2yo',
 *        going: ['HD', 'HY', 'GF']
 *    },
 *    {
 *        raceType: ['ire-jumps', 'ire-flat']
 *    }
 * ]
 *
 * will be treated in this way:
 *
 * (
 *    (raceType = flat OR raceType = jumps) AND
 *    age = 2yo AND
 *    ( goind = HD OR goind = HY OR goind = GF )
 * ) OR (
 *    raceType = ire-jumps OR
 *    raceType = ire-flat
 * )
 */
function checkConditions(conditions, selected) {
    if (!conditions) {
        return true;
    }

    return conditions.map(condition =>
        Object.keys(condition).map(name => (
            condition[name] instanceof Array
                ? condition[name].some(val => selected[name] === val)
                : selected[name] === condition[name]
        )).every(val => val)
    ).some(val => val);
}

function getHiddenParams(selected, parameters) {
    return parameters.filter(param =>
        !checkConditions(param.conditions, selected)
    ).map(param => param.name);
}

function applySelection(selected, parameters) {
    const selection = {};
    const hiddenParams = getHiddenParams(selected, parameters);
    Object.keys(selected).map((name) => {
        const parameter = parameters.find(param => name === param.name);
        if (hiddenParams.indexOf(name) === -1) {
            selection[parameter.param || parameter.name] = selected[name];
        }
        return true;
    });

    return selection;
}

function buildStatus(selected, parameters) {
    const status = [];
    const hiddenParams = getHiddenParams(selected, parameters);
    Object.keys(selected).map((name) => {
        const parameter = parameters.find(param => name === param.name);
        if (hiddenParams.indexOf(name) === -1) {
            status.push({
                parameter,
                option: parameter.options.find(option => option.value === selected[name])
            });
        }
        return true;
    });

    return status;
}

export default function reducer(state = initialState, action) {
    let selection;
    let expandFilters;
    let initState;
    switch (action.type) {
        case FILTER_INIT:
            initState = Object.assign(
                {},
                initialState,
                state,
                action.config
            );

            return Object.assign({}, initState, {
                initialSelection: initState.defaults,
                selection: initState.defaults,
                applied: [],
                status: [],
                hiddenParams: getHiddenParams(
                    initState.defaults,
                    initState.parameters
                )
            });

        case FILTER_TOGGLE:
            expandFilters = state.parameters
                .map(filterItem => (filterItem.expand ? filterItem.name : false))
                .filter(filterItem => !!filterItem);
            return Object.assign({}, state, {
                opened: !state.opened,
                expanded: expandFilters,
                selection: state.initialSelection,
                hiddenParams: getHiddenParams(state.initialSelection, state.parameters)
            });

        case FILTER_CLOSE:
            return Object.assign({}, state, {
                opened: false
            });

        case FILTER_PARAMETER_TOGGLE:
            return Object.assign({}, state, {
                expanded: state.expanded.indexOf(action.name) === -1
                    ? state.expanded.concat([action.name])
                    : state.expanded.filter(name => name !== action.name)
            });

        case FILTER_OPTION_SELECT:
            selection = Object.assign({}, state.selection, { [action.name]: action.value });

            return Object.assign({}, state, {
                selection,
                expanded: state.expanded.filter(name => name !== action.name),
                hiddenParams: getHiddenParams(selection, state.parameters)
            });

        case FILTER_APPLY:
            return Object.assign({}, state, {
                opened: false,
                initialSelection: state.selection,
                applied: applySelection(state.selection, state.parameters),
                status: buildStatus(state.selection, state.parameters)
            });

        case FILTER_CONFIG_REQUEST:
            return Object.assign({}, state, initialState);

        case FILTER_CONFIG_UPDATE:
            return _.mergeWith(
                {},
                state,
                action.defaultConfig,
                action.config,
                (objValue, srcValue) => (
                    _.isArray(objValue)
                        ? _.unionBy(objValue, srcValue, 'name')
                        : undefined
                )
            );

        default:
            return state;
    }
}

export function createReducer(stateKey, config = {}) {
    return (state = Object.assign({}, initialState, config), action) => {
        if (!action.stateKey || action.stateKey !== stateKey) {
            return state;
        }

        return reducer(state, action);
    };
}



// WEBPACK FOOTER //
// ./src/js/modules/Filter/reducers/index.js